// src/components/BookCard.jsx
import React, { useEffect, useState, memo } from "react";
import { titleOf, thumbUrlOf } from "../lib/omekaClient";
import { useTranslation } from "react-i18next";
import { Info, X } from "lucide-react";

const thumbCache = new Map();

function CoverPlaceholder({ label }) {
  return (
    <div className="w-full h-full grid place-items-center rounded-lg bg-gradient-to-br from-black/5 to-black/10">
      <div className="flex flex-col items-center gap-2 text-[11px] md:text-xs text-black/50 text-center px-2">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M4.5 5.5A2.5 2.5 0 0 1 7 3h11v16H7a2.5 2.5 0 0 0-2.5 2.5V5.5Z" stroke="currentColor" strokeWidth="1.4" />
          <path d="M7 6h7M7 9h8" stroke="currentColor" strokeWidth="1.4" />
        </svg>
        <span>{label}</span>
      </div>
    </div>
  );
}

function BookCardInner({ item, onOpen, compact = false, titleOverride, descOverride }) {
  const { t, i18n } = useTranslation();
  const [imgUrl, setImgUrl] = useState(null);
  const [hasImg, setHasImg] = useState(false);
  const [loading, setLoading] = useState(true);

  // State สำหรับควบคุมการเปิด/ปิด Overlay ด้วยปุ่ม
  const [showInfo, setShowInfo] = useState(false);

  // --- Helper: ดึงค่าข้อมูลจาก item ---
  const getFieldValue = (fieldKey, directKey) => {
    if (!item) return "";
    if (item[directKey]) return item[directKey];

    const field = item[fieldKey];
    if (!Array.isArray(field)) return "";

    const L = (i18n.language || "").toLowerCase();
    const hit = field.find(x => (x?.["@language"] || "").toLowerCase() === L);
    return hit?.["@value"] || field[0]?.["@value"] || "";
  };

  // ดึงข้อมูลทั้ง 15 หัวข้อเตรียมไว้
  const itemTitle = titleOverride || getFieldValue("dcterms:title", "title") || titleOf(item) || item.title;
  const itemAlternative = getFieldValue("dcterms:alternative", "alternative");
  const itemCreator = getFieldValue("dcterms:creator", "creator");
  const itemContributor = getFieldValue("dcterms:contributor", "contributor");
  const itemPublisher = getFieldValue("dcterms:publisher", "publisher");
  const itemDate = getFieldValue("dcterms:date", "date");
  const itemDescription = getFieldValue("dcterms:description", "description");
  const itemSubject = getFieldValue("dcterms:subject", "subject");
  const itemType = getFieldValue("dcterms:type", "type");
  const itemFormat = getFieldValue("dcterms:format", "format");
  const itemLanguage = getFieldValue("dcterms:language", "language");
  const itemIdentifier = getFieldValue("dcterms:identifier", "identifier");
  const itemSource = getFieldValue("dcterms:source", "source");
  const itemRelation = getFieldValue("dcterms:relation", "relation");
  const itemCoverage = getFieldValue("dcterms:coverage", "coverage");
  const itemRights = getFieldValue("dcterms:rights", "rights");

  // จัดกลุ่มข้อมูลเพื่อง่ายต่อการ Loop แสดงผล
  const metadataList = [
    { label: t("bookCard.metadata.title"), value: itemTitle },
    { label: t("bookCard.metadata.alternative"), value: itemAlternative },
    { label: t("bookCard.metadata.creator"), value: itemCreator },
    { label: t("bookCard.metadata.contributor"), value: itemContributor },
    { label: t("bookCard.metadata.publisher"), value: itemPublisher },
    { label: t("bookCard.metadata.date"), value: itemDate },
    { label: t("bookCard.metadata.subject"), value: itemSubject },
    { label: t("bookCard.metadata.type"), value: itemType },
    { label: t("bookCard.metadata.format"), value: itemFormat },
    { label: t("bookCard.metadata.language"), value: itemLanguage },
    { label: t("bookCard.metadata.identifier"), value: itemIdentifier },
    { label: t("bookCard.metadata.source"), value: itemSource },
    { label: t("bookCard.metadata.relation"), value: itemRelation },
    { label: t("bookCard.metadata.coverage"), value: itemCoverage },
    { label: t("bookCard.metadata.rights"), value: itemRights },
    { label: t("bookCard.metadata.description"), value: itemDescription },
  ].filter(meta => meta.value && meta.value.trim() !== "");

  useEffect(() => {
    let active = true;
    if (!item) { setLoading(false); return; }

    setLoading(true);

    if (item.cover) {
      setImgUrl(item.cover);
      setHasImg(true);
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const id = item?.["o:id"] || item?.id;
        if (id && thumbCache.has(id)) {
          if (!active) return;
          const cached = thumbCache.get(id);
          setImgUrl(cached);
          setHasImg(!!cached);
          setLoading(false);
          return;
        }

        const url = await thumbUrlOf(item);
        if (!active) return;

        if (id) thumbCache.set(id, url || null);
        if (url) {
          setImgUrl(url);
          setHasImg(true);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => { active = false; };
  }, [item]);

  const title = itemTitle || t("books.untitled", "ไม่มีชื่อเรื่อง");
  const desc = descOverride || "";

  // ฟังก์ชันเปิดอ่านหนังสือ
  const handleOpen = () => { if (onOpen) onOpen(item); };

  // ฟังก์ชันเปิด/ปิด ข้อมูลเพิ่มเติม (ป้องกันไม่ให้การคลิกทะลุไปเปิดหนังสือ)
  const toggleInfo = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowInfo(!showInfo);
  };

  return (
    <div onClick={handleOpen} className={`${compact ? "flex gap-3 w-full text-left hover:bg-black/5 p-2" : "group flex flex-col w-full h-full text-left rounded-2xl overflow-hidden ring-1 ring-black/10 bg-white hover:shadow-xl"} transition cursor-pointer`}>
      <div className={`${compact ? "w-16 h-24 rounded-lg flex-none" : "relative aspect-[3/4]"} bg-black/5 overflow-hidden`}>
        {loading ? (
          <div className="w-full h-full animate-pulse bg-black/10" />
        ) : hasImg && imgUrl ? (
          <img src={imgUrl} alt={title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <CoverPlaceholder label={t("bookCard.noCover")} />
        )}

        {/* --- ส่วน Hover Overlay (แสดงข้อมูล 15 รายการ) --- */}
        {!compact && (
          <div
            className={`absolute inset-0 bg-black/85 backdrop-blur-sm transition-opacity duration-300 z-20 p-4 overflow-y-auto ${showInfo ? "opacity-100 pointer-events-auto" : "opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"}`}
            style={{ scrollbarWidth: 'thin' }}
          >
            <div className={`flex flex-col gap-2 text-left transition-transform duration-300 min-h-full ${showInfo ? "translate-y-0" : "translate-y-2 group-hover:translate-y-0"}`}>
              {metadataList.length > 0 ? (
                metadataList.map((meta, index) => (
                  <div key={index} className="text-[10px] md:text-xs leading-tight text-white/90">
                    <span className="font-bold text-white/60 block text-[9px] mb-0.5">{meta.label}</span>
                    <span className="line-clamp-3">{meta.value}</span>
                  </div>
                ))
              ) : (
                <div className="flex items-center justify-center h-full text-sm font-semibold text-white/90 tracking-wide">
                  {t("actions.open", "คลิกเพื่ออ่าน")}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {!compact && (
        <div className="p-3 flex flex-col flex-grow justify-between">


          {/* --- ปุ่ม แสดงรายละเอียดเพิ่มเติม แบบเห็นได้ชัดเจน --- */}
          <button
            onClick={toggleInfo}
            className={`mt-3 w-full py-2 rounded-xl text-[11px] md:text-xs font-bold transition-colors flex items-center justify-center gap-1.5 ${showInfo ? "bg-[#5b4a3e] text-white" : "bg-[#5b4a3e]/5 text-[#5b4a3e] hover:bg-[#5b4a3e]/10"}`}
          >
            {showInfo ? <X size={14} /> : <Info size={14} />}
            {showInfo ? t("bookCard.hideDetails") : t("bookCard.moreDetails")}
          </button>
        </div>
      )}

      {compact && (
        <div className="flex-1">
          <div className="font-semibold leading-snug line-clamp-2">{title}</div>
          <div className="text-sm opacity-60">{t("actions.open", "คลิกเพื่ออ่าน")}</div>
        </div>
      )}
    </div>
  );
}

export default memo(BookCardInner);